import moment from 'moment';
import axios from 'axios';
import API from '../config/API';
import { hairdresserService } from './hairdresser.service';
import { paymentService } from './payment.service';
import { productService } from './product.service';
import { getOwnHairdresserId, isManagerRole } from '../component/notifications/role';

// Auth headers helper for the new server-backed endpoints.
const _v2Headers = () => {
  const accessToken = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    authorization: 'Basic YmV1YXBwOmJldWFwcA==',
    accessToken: `${accessToken}`,
  };
};

// Map BE notification type → an icon identifier the FE renderer
// already understands (calendar, x, payment, etc).
const ICON_BY_TYPE = {
  booking_created: 'calendar',
  booking_cancelled: 'x',
  booking_no_show: 'x',
  booking_rescheduled: 'calendar',
  payment_captured: 'payment',
  tip_received: 'payment',
  refund_processed: 'payment',
  voucher_redeemed: 'payment',
  stock_low: 'package',
  stock_out: 'package',
  client_new: 'user',
  staff_added: 'user',
  staff_removed: 'user',
  diary_shared: 'eye',
  diary_unshared: 'eye-off',
};

/**
 * Map a server notification row into the FE item shape components were
 * built against (id/type/title/subtitle/timestamp/icon/linkTo/meta).
 * Keeps NotificationPanel + NotificationItem rendering unchanged after
 * the data-source swap.
 */
const _serverToItem = (row) => {
  const ts = row.createdAt ? new Date(row.createdAt).getTime() : Date.now();
  return {
    id: row.id,
    type: row.type,
    title: row.title || '',
    // Multi-location: which site the event happened at (set by the API for
    // every row; the bell prefixes the title with it for multi-site users).
    salonName: row.salonName || null,
    subtitle: row.body || '',
    timestamp: ts,
    icon: ICON_BY_TYPE[row.type] || 'bell',
    linkTo: row.link
      ? { path: row.link, state: row.data || {} }
      : null,
    meta: row.data || {},
    isRead: !!row.isRead,
    // Server-backed row: `id` is a real notification _id, so it can be marked
    // read individually. Legacy client-derived rows (the fallback path) carry
    // synthetic ids like 'booking:<id>:created' and must NEVER be sent to the
    // per-notification read endpoint.
    isServerRow: true,
    importance: row.importance || 'normal',
    sensitivity: row.sensitivity || 'public',
  };
};

const LOOKBACK_HOURS = 48;

const safeCall = async (promise) => {
  try {
    const res = await promise;
    return res;
  } catch {
    return null;
  }
};

const unwrap = (res, key) => {
  const d = res?.data?.data || res?.data?.responseData || res?.data || {};
  return d?.[key] || d;
};

/**
 * Fetch diary bookings for today + a small window so we can surface "new booking"
 * notifications (based on createdAt) and cancellations (based on cancelledAt).
 */
const fetchRecentBookings = async () => {
  const startDate = moment().subtract(1, 'day').format('YYYY-MM-DD');
  const endDate = moment().add(7, 'days').format('YYYY-MM-DD');
  const res = await safeCall(hairdresserService.getDiaryData({ startDate, endDate }));
  if (!res) return [];
  const diaryData = res?.data?.responseData || res?.responseData || res?.data || {};
  const staff = diaryData?.staff || [];
  const out = [];
  staff.forEach(s => {
    const staffId = s?.hairdresserId || s?._id || s?.id;
    const staffName = s?.name || s?.hairdresserName || '';
    const bookings = s?.bookings || s?.events || [];
    bookings.forEach(b => {
      out.push({
        ...b,
        _staffId: staffId,
        _staffName: staffName,
      });
    });
  });
  return out;
};

/**
 * Fetch recent transactions for tip + payment notifications.
 */
const fetchRecentTransactions = async () => {
  const startDate = moment().subtract(2, 'days').format('YYYY-MM-DD');
  const endDate = moment().format('YYYY-MM-DD');
  const res = await safeCall(paymentService.paymentList({
    activePage: 1,
    limit: 50,
    itemsCountPerPage: 50,
    count: 50,
    hairdresserId: '',
    sortField: 'createdAt',
    sortType: 'desc',
    search: '',
    startDate,
    endDate,
    isDownload: 0,
    selectedValue: [],
    selectedBookingStatus: [],
  }));
  if (!res) return [];
  const d = res?.data?.responseData || res?.data?.data || {};
  return d?.paymentList || d?.result || d?.data || [];
};

/**
 * Fetch products and return those at/below their stockMinimum.
 */
const fetchLowStockProducts = async () => {
  const salonId = localStorage.getItem('salonId') || '';
  const res = await safeCall(productService.getProductsList({ salonId, search: '' }));
  if (!res) return [];
  const pd = res?.data?.data || res?.data?.responseData || {};
  let list = pd?.products || pd?.productList || pd?.result || [];
  if (!Array.isArray(list)) list = [];
  return list.filter(p => {
    if (!p || p.isDeleted || p.deleted || p.isActive === false) return false;
    const stock = Number(p.stock ?? p.stockCount ?? 0);
    const min = Number(p.stockMinimum ?? p.minStock ?? 0);
    return min > 0 && stock <= min;
  });
};

const asEpoch = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000;
  const m = moment(value);
  return m.isValid() ? m.valueOf() : 0;
};

const formatName = (b) => {
  return b?.userId?.name
    || b?.clientName
    || b?.client?.name
    || b?.user?.name
    || 'A client';
};

const formatServiceName = (b) => {
  const svc = b?.bookingSlots?.[0]?.service?.serviceName
    || b?.bookingSlots?.[0]?.serviceName
    || b?.serviceName
    || b?.service?.serviceName;
  return svc || 'a service';
};

/**
 * Build a notification object from a booking row based on which event fired.
 */
const buildBookingNotification = (booking, eventType) => {
  const bookingId = booking?._id || booking?.bookingId || booking?.id;
  if (!bookingId) return null;
  const created = asEpoch(booking.createdAt);
  const cancelled = asEpoch(booking.cancelledAt || booking.cancelDate);
  const rescheduled = asEpoch(booking.rescheduledAt || booking.updatedAt);
  const clientName = formatName(booking);
  const serviceName = formatServiceName(booking);
  const bookingDate = booking?.bookingDate
    ? moment(booking.bookingDate).format('ddd D MMM')
    : '';

  if (eventType === 'created') {
    return {
      id: `booking:${bookingId}:created`,
      type: 'booking_created',
      title: 'New booking',
      subtitle: `${clientName} · ${serviceName}${bookingDate ? ` · ${bookingDate}` : ''}`,
      timestamp: created || Date.now(),
      icon: 'calendar',
      linkTo: {
        path: `/diary/${localStorage.getItem('salonId') || ''}`,
        state: { bookingDate: booking.bookingDate, openBookingId: bookingId },
      },
      meta: { bookingId, staffId: booking._staffId },
    };
  }
  if (eventType === 'cancelled') {
    return {
      id: `booking:${bookingId}:cancelled`,
      type: 'booking_cancelled',
      title: 'Booking cancelled',
      subtitle: `${clientName} · ${serviceName}${bookingDate ? ` · ${bookingDate}` : ''}`,
      timestamp: cancelled || Date.now(),
      icon: 'x',
      linkTo: {
        path: `/diary/${localStorage.getItem('salonId') || ''}`,
        state: { bookingDate: booking.bookingDate, openBookingId: bookingId },
      },
      meta: { bookingId, staffId: booking._staffId },
    };
  }
  if (eventType === 'rescheduled') {
    return {
      id: `booking:${bookingId}:rescheduled`,
      type: 'booking_rescheduled',
      title: 'Booking moved',
      subtitle: `${clientName} · ${serviceName}${bookingDate ? ` · ${bookingDate}` : ''}`,
      timestamp: rescheduled || Date.now(),
      icon: 'swap',
      linkTo: {
        path: `/diary/${localStorage.getItem('salonId') || ''}`,
        state: { bookingDate: booking.bookingDate, openBookingId: bookingId },
      },
      meta: { bookingId, staffId: booking._staffId },
    };
  }
  return null;
};

const buildPaymentNotifications = (txn, currencySymbol) => {
  const txnId = txn?._id || txn?.transactionId || txn?.id;
  if (!txnId) return [];
  const created = asEpoch(txn.createdAt || txn.transactionDate);
  const out = [];
  const bookingId = txn?.bookingId || txn?.booking?._id;
  const pathBase = bookingId
    ? { path: `/checkout/${bookingId}`, state: {} }
    : { path: '/transactions', state: {} };

  const amount = Number(txn?.amount ?? txn?.totalAmount ?? 0);
  const tip = Number(txn?.tipAmount ?? txn?.tip ?? 0);
  const sym = currencySymbol || '£';

  if (amount > 0) {
    out.push({
      id: `payment:${txnId}`,
      type: 'payment_received',
      title: 'Payment received',
      subtitle: `${sym}${(amount / 100).toFixed(2)} · ${formatName(txn)}`,
      timestamp: created || Date.now(),
      icon: 'card',
      linkTo: pathBase,
      meta: { transactionId: txnId, hairdresserId: txn?.hairdresserId },
    });
  }
  if (tip > 0) {
    out.push({
      id: `tip:${txnId}`,
      type: 'tip_received',
      title: 'Tip received',
      subtitle: `${sym}${(tip / 100).toFixed(2)} · ${formatName(txn)}`,
      timestamp: created || Date.now(),
      icon: 'heart',
      linkTo: pathBase,
      meta: { transactionId: txnId, hairdresserId: txn?.hairdresserId },
    });
  }
  return out;
};

const buildLowStockNotification = (product) => {
  const productId = product?._id || product?.id || product?.productId;
  if (!productId) return null;
  const dayKey = moment().format('YYYY-MM-DD');
  return {
    id: `lowstock:${productId}:${dayKey}`,
    type: 'low_stock',
    title: 'Low stock',
    subtitle: `${product.productName || product.name || 'A product'} · ${product.stock ?? 0} left`,
    timestamp: Date.now(),
    icon: 'box',
    linkTo: {
      path: '/product/editProduct',
      state: { product },
    },
    meta: { productId },
  };
};

/**
 * Role-filter + lookback-filter to keep the feed relevant.
 */
const filterByRole = (items, role, ownId) => {
  if (isManagerRole(role)) return items;
  return items.filter(item => {
    if (item.type === 'low_stock') return false;
    const staffId = item.meta?.staffId || item.meta?.hairdresserId;
    if (!staffId || !ownId) return true;
    return String(staffId) === String(ownId);
  });
};

const filterByLookback = (items) => {
  const cutoff = Date.now() - LOOKBACK_HOURS * 60 * 60 * 1000;
  return items.filter(i => i.timestamp >= cutoff);
};

/**
 * Detect diary sharing changes by comparing the current sharing state
 * against a locally-stored snapshot. Returns notification items for any
 * staff whose sharing status flipped since the last check.
 */
const DIARY_SHARE_SNAPSHOT_KEY = () => {
  const userId = localStorage.getItem('userId') || 'anon';
  return `notifications:diaryShareSnapshot:${userId}`;
};

const fetchDiarySharingChanges = async () => {
  try {
    const { diarySharingService } = await import('./diarySharing.service');
    const data = await diarySharingService.fetchSettings();
    const key = DIARY_SHARE_SNAPSHOT_KEY();
    const prevRaw = localStorage.getItem(key);
    const prev = prevRaw ? JSON.parse(prevRaw) : {};

    const changes = [];
    (data.staff || []).forEach(s => {
      const prevShares = prev[s.hairdresserId];
      if (prevShares === undefined) return; // first run, no baseline
      if (s.sharesWithCurrentUser && !prevShares) {
        changes.push({
          id: `diary_shared:${s.hairdresserId}:${Date.now()}`,
          type: 'diary_shared',
          title: 'Diary now visible',
          subtitle: `${s.name} shared their diary with you`,
          timestamp: Date.now(),
          icon: 'calendar',
          linkTo: { path: '/settings', state: {} },
          meta: { staffId: s.hairdresserId },
        });
      } else if (!s.sharesWithCurrentUser && prevShares) {
        changes.push({
          id: `diary_unshared:${s.hairdresserId}:${Date.now()}`,
          type: 'diary_unshared',
          title: 'Diary access removed',
          subtitle: `${s.name} stopped sharing their diary with you`,
          timestamp: Date.now(),
          icon: 'x',
          linkTo: { path: '/settings', state: {} },
          meta: { staffId: s.hairdresserId },
        });
      }
    });

    // Save new snapshot
    const snapshot = {};
    (data.staff || []).forEach(s => {
      snapshot[s.hairdresserId] = !!s.sharesWithCurrentUser;
    });
    localStorage.setItem(key, JSON.stringify(snapshot));

    return changes;
  } catch {
    return [];
  }
};

/**
 * Main entry. Returns a promise that resolves to a sorted, deduped,
 * role-filtered array of notifications.
 */
export const deriveNotifications = async ({ user, role, currencySymbol } = {}) => {
  // Server-backed path: hit /notification-v2 and map to the existing FE
  // item shape. The BE already filters by user/sensitivity (reception
  // mode honoured via excludeSensitive query param) and returns the
  // newest 50 — far more efficient than the previous client-side
  // derivation which polled bookings + payments + products every 60s.
  try {
    const excludeSensitive = (() => {
      try {
        const settings = JSON.parse(localStorage.getItem('receptionModeSettings') || 'null');
        return !!(settings && settings.enabled);
      } catch { return false; }
    })();

    const params = new URLSearchParams();
    params.set('limit', '50');
    if (excludeSensitive) params.set('excludeSensitive', 'true');

    const res = await axios.get(`${API.NOTIFICATION_V2_LIST}?${params.toString()}`, {
      headers: _v2Headers(),
    });
    const payload = res?.data?.responseData?.result || res?.data?.responseData || res?.data?.data || {};
    const items = Array.isArray(payload.items) ? payload.items : [];
    return items.map(_serverToItem);
  } catch (err) {
    // If the new endpoint isn't available (e.g. older BE deploy lagging),
    // fall back to the original client-derivation path below so we don't
    // regress for anyone hitting an unconverted server.
    console.warn('[notifications] /notification-v2 fetch failed, falling back to client derivation', err?.message);
    return _legacyDeriveNotifications({ user, role, currencySymbol });
  }
};

// Original client-side derivation, kept as a fallback while the BE
// rolls out and during local dev against an older API. Will be removed
// once the BE is confirmed live everywhere.
const _legacyDeriveNotifications = async ({ user, role, currencySymbol } = {}) => {
  const ownId = getOwnHairdresserId(user);
  const [bookings, txns, lowStock, diaryChanges] = await Promise.all([
    fetchRecentBookings(),
    fetchRecentTransactions(),
    isManagerRole(role) ? fetchLowStockProducts() : Promise.resolve([]),
    fetchDiarySharingChanges(),
  ]);

  const items = [];
  const cutoff = Date.now() - LOOKBACK_HOURS * 60 * 60 * 1000;

  bookings.forEach(b => {
    if (asEpoch(b.createdAt) >= cutoff) {
      const n = buildBookingNotification(b, 'created');
      if (n) items.push(n);
    }
    const status = String(b.status || '').toLowerCase();
    const cancelledTs = asEpoch(b.cancelledAt || b.cancelDate);
    if ((status === 'cancelled' || status === 'canceled' || b.isCancelled) && cancelledTs >= cutoff) {
      const n = buildBookingNotification(b, 'cancelled');
      if (n) items.push(n);
    }
    if (asEpoch(b.rescheduledAt) >= cutoff) {
      const n = buildBookingNotification(b, 'rescheduled');
      if (n) items.push(n);
    }
  });

  txns.forEach(t => {
    buildPaymentNotifications(t, currencySymbol).forEach(n => items.push(n));
  });

  lowStock.forEach(p => {
    const n = buildLowStockNotification(p);
    if (n) items.push(n);
  });

  diaryChanges.forEach(n => items.push(n));

  const deduped = [];
  const seen = new Set();
  items.forEach(i => {
    if (seen.has(i.id)) return;
    seen.add(i.id);
    deduped.push(i);
  });

  const filtered = filterByLookback(filterByRole(deduped, role, ownId));
  filtered.sort((a, b) => b.timestamp - a.timestamp);
  return filtered;
};

/**
 * Mark a single notification as read on the server. Returns the modified
 * count (0 if already read, 1 if just flipped). Component callers should
 * optimistically update local state and then call this to persist.
 */
export const fetchNotificationPreferences = async () => {
  const res = await axios.get(API.NOTIFICATION_V2_LIST + '/preferences', { headers: _v2Headers() });
  return res?.data?.responseData || null;
};

export const updateNotificationPreferences = async (patch) => {
  const res = await axios.patch(API.NOTIFICATION_V2_LIST + '/preferences', patch, { headers: _v2Headers() });
  return res?.data?.responseData || null;
};

export const markNotificationRead = async (notificationId) => {
  try {
    const url = `${API.NOTIFICATION_V2_READ}${notificationId}/read`;
    const res = await axios.patch(url, {}, { headers: _v2Headers() });
    return res?.data?.responseData?.result?.modified || 0;
  } catch (err) {
    console.warn('[notifications] markNotificationRead failed', err?.message);
    return 0;
  }
};

/**
 * Mark all notifications as read for the current user. Returns count.
 */
export const markAllNotificationsRead = async () => {
  try {
    const res = await axios.patch(API.NOTIFICATION_V2_MARK_ALL_READ, {}, {
      headers: _v2Headers(),
    });
    return res?.data?.responseData?.result?.modified || 0;
  } catch (err) {
    console.warn('[notifications] markAllNotificationsRead failed', err?.message);
    return 0;
  }
};

/**
 * Unread count for the bell badge — server-side count, honours reception
 * mode via excludeSensitive when active.
 */
export const fetchUnreadCount = async () => {
  try {
    const excludeSensitive = (() => {
      try {
        const settings = JSON.parse(localStorage.getItem('receptionModeSettings') || 'null');
        return !!(settings && settings.enabled);
      } catch { return false; }
    })();
    const url = excludeSensitive
      ? `${API.NOTIFICATION_V2_UNREAD_COUNT}?excludeSensitive=true`
      : API.NOTIFICATION_V2_UNREAD_COUNT;
    const res = await axios.get(url, { headers: _v2Headers() });
    // The endpoint returns the count on responseData directly; the nested
    // `result` shape is kept as a fallback in case the envelope changes.
    const data = res?.data?.responseData;
    const count = data?.count ?? data?.result?.count;
    return typeof count === 'number' ? count : null;
  } catch (err) {
    // null, not 0: a failed request says nothing about the count, and a 0
    // made the bell badge drop and come back with the next poll
    return null;
  }
};
